import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Sparkles, X, ChevronUp } from "lucide-react";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const fmt = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e.msg || JSON.stringify(e)).join(" ") : d?.msg || "Something went wrong");

// Floating claim panel: appears on the invitee's OWN page when opened via ?invite=TOKEN. Creates the verified-able account in place.
export const FloatingClaim = () => {
  const [params, setParams] = useSearchParams();
  const token = params.get("invite");
  const loc = useLocation();
  const nav = useNavigate();
  const { user, login } = useAuth();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [min, setMin] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({ email: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    setInv(null); setErr(""); setDone(false);
    axios.get(`${API}/invites/${token}`).then((r) => { setInv(r.data); setF((x) => ({ ...x, email: r.data.email || "", name: r.data.name || "" })); })
      .catch((e) => setErr(fmt(e.response?.data?.detail) || "Invalid invite"));
  }, [token]);

  if (!token || done) return null;

  const accept = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/invites/${token}/accept`, f, { withCredentials: true });
      login(r.data.user, r.data.token);
      toast.success(`This page is yours now, ${r.data.user.name?.split(" ")[0] || ""} — look for the owner buttons`);
      setDone(true);
      params.delete("invite"); setParams(params, { replace: true });
      setTimeout(() => window.location.reload(), 600);
    } catch (e2) {
      const d = fmt(e2.response?.data?.detail);
      if (d.startsWith("SIGN_IN_FIRST")) { toast.message("That email already has an account — sign in, then open your link again."); nav(`/auth?next=${encodeURIComponent(loc.pathname + loc.search)}`); return; }
      setErr(d);
    } finally { setBusy(false); }
  };

  return (
    <div className={`fixed z-[1300] right-3 left-3 sm:left-auto sm:right-5 bottom-24 sm:bottom-6 sm:w-[380px] max-h-[70vh] overflow-y-auto rounded-3xl border-2 border-[#F2CC8F] bg-[#0A0A0F] text-white shadow-2xl transition-all ${min ? "p-3" : "p-5"}`} data-testid="floating-claim">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setMin((m) => !m)} role="button" aria-label={min ? "Expand invite" : "Minimize invite"}>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F2CC8F] flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> Private invite · THE WALL</div>
          {inv && <div className="font-['Playfair_Display'] text-xl font-semibold leading-tight mt-0.5" data-testid="floating-claim-title">This is your page, <span className="text-[#F2CC8F]">{inv.name}</span>. Claim it.</div>}
        </div>
        <button type="button" onClick={() => setMin((m) => !m)} aria-label={min ? "Expand" : "Minimize"} className="shrink-0 inline-flex items-center gap-1 rounded-full border border-white/30 px-3 min-h-[44px] min-w-[44px] text-xs font-bold text-white/90 hover:bg-white/10" data-testid="floating-claim-min">{min ? <><ChevronUp className="h-5 w-5" /> Open</> : <><X className="h-5 w-5" /> Minimize</>}</button>
      </div>
      {!min && (
        err && !inv ? (
          <p className="mt-3 text-sm text-red-300" data-testid="floating-claim-error">{err} <Link to="/auth" className="underline text-[#54E3FF]">Sign in</Link></p>
        ) : !inv ? <p className="mt-3 text-sm text-white/70">Checking your invite…</p> : (
          <form onSubmit={accept} className="mt-3 space-y-2" data-testid="floating-claim-form">
            <p className="text-xs text-white/75">Set your email + password → you're signed in and this page gets owner buttons: add shows, EPK, music, calendar sync. Verify your email from your StarGate to publish.</p>
            {user && <p className="text-[11px] text-[#54E3FF]">Signed in as {user.email} — accept with that email to link this page to your account.</p>}
            <Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder={inv.email_hint ? `Email (${inv.email_hint})` : "Your email"} readOnly={!!inv.email} className="h-9 bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="floating-claim-email" />
            <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name (or band name)" className="h-9 bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="floating-claim-name" />
            <Input type="password" required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Choose a password (8+ characters)" className="h-9 bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="floating-claim-password" />
            {err && <p className="text-xs text-red-300" data-testid="floating-claim-error">{err}</p>}
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#F2CC8F] text-[#120A24] hover:bg-[#e6b96f] font-bold h-10" data-testid="floating-claim-accept"><KeyRound className="h-4 w-4 mr-2" /> {busy ? "Claiming…" : "Claim my page & sign in"}</Button>
            <p className="text-[10px] text-white/50">One-time link · expires {new Date(inv.expires_at).toLocaleDateString()} · <Link to={`/auth?next=${encodeURIComponent(loc.pathname + loc.search)}`} className="underline">Already have an account?</Link></p>
          </form>
        )
      )}
    </div>
  );
};
